<template>
  <LKLayout class="BLayoutLK">
    <template #header>
      <LKHeader
        :profile="profile"
        :authorized="authorized"
        :reportless="!hasAccessToReport"
        :user-permission-label="userPermissionLabel"
        :access-to-scan="accessToScan"
      >
        <template #notifications>
          <NotificationsMenu
            v-if="authorized"
            class-button="BLayoutLK__notifications nav__button nav__button-rounded"
          />
        </template>
      </LKHeader>
    </template>

    <template v-if="hasAsideMenu" #aside>
      <LKAsideMenu
        :topics="topics"
        :authorized="authorized"
        :roadmaps-count="roadmapsCount"
        :mun-meh-count="munMehCount"
        :reg-meh-count="regMehCount"
        :loading-mun-meh-count="isLoadingMunMehCount"
        :loading-reg-meh-count="isLoadingRgMehCount"
        :loading-roadmaps-count="isLoadingRoadmapsCount"
      />
    </template>

    <template #before-content>
      <LKBanners
        :list="banners"
        :active.sync="isActiveBanners"
        :new-count="newBannersCount"
        @update:active="toggleBanners"
        @update:viewed="onBannersView"
      />
    </template>

    <transition name="fade-slow" mode="out-in">
      <router-view
        v-if="ready"
      />

      <LKProgressCircular
        v-else
        overlay
        :size="50"
        style="align-items: start; padding-top: 30vh;"
      />
    </transition>

    <portal-target
      name="app-outlet"
    />

    <template #footer>
      <LKFooter v-once />
    </template>
  </LKLayout>
</template>

<script>
import { rootMapper } from '@/store/modules/root'
import { lkMapper } from '@/store/modules/lk'
import { lkRoadmapPermissions as Permissions, ixoraViewPermissions as ixoraScan } from '@/helpers/enums/permission-models'
import { StorageService } from '@/helpers/storage.service'
import { LK_REPORT_ROUTES } from '@/views/LKReports/utils'
import { LK_ROADMAPS_FEED_ROUTES } from '@/views/LKRoadmapsFeed/utils'

import LKProgressCircular from '@/components/lk/LKProgressCircular.vue'
import LKLayout from './components/BLayoutLK/LKLayout.vue'
import LKHeader from './components/BLayoutLK/LKHeader.vue'
import LKAsideMenu from './components/BLayoutLK/LKAsideMenu.vue'
import LKBanners from './components/BLayoutLK/LKBanners.vue'
import LKFooter from './components/BLayoutLK/LKFooter.vue'
import { LK_PUBLICATIONS_FORMDATA_ROUTES } from '@/views/LKPublications/formdata/utils'
import { LK_PASSPORT_ROUTES } from '@/views/LKPassport/utils'
import { LK_MUN_MEH_FORM_ROUTES } from '@/views/LKMunMeh/Form/utils'
import { LK_MUN_MEH_FORM_REVIEW_ROUTES } from '@/views/LKMunMeh/Review/utils'
import { LK_MUN_MEH_FORM_REVIEW_SECOND_ROUTES } from '@/views/LKMunMeh/ReviewSecond/utils'
import { LK_MUN_MEH_FORM_HOST_VIEW_ROUTES } from '@/views/LKMunMeh/HostView/utils'
import { getProjectName } from '@/helpers/projectName'


const LABELS = {
  ADRESAT: 'Личный кабинет образовательной организации',
  CURATOR: 'Личный кабинет куратора',
  COORDINATOR: 'Личный кабинет координатора',
}

const EXCLUDE_ASIDE = [
  LK_REPORT_ROUTES.VIEW,
  LK_PUBLICATIONS_FORMDATA_ROUTES.FORM,
  LK_PASSPORT_ROUTES.LAYOUT,
  LK_MUN_MEH_FORM_ROUTES.FORM,
  LK_MUN_MEH_FORM_ROUTES.ANY_LEVEL,
  LK_MUN_MEH_FORM_REVIEW_ROUTES.FORM,
  LK_MUN_MEH_FORM_HOST_VIEW_ROUTES.FORM,
  LK_MUN_MEH_FORM_REVIEW_ROUTES.ANY_LEVEL,
  LK_MUN_MEH_FORM_REVIEW_SECOND_ROUTES.FORM,
  LK_MUN_MEH_FORM_HOST_VIEW_ROUTES.ANY_LEVEL,
  LK_MUN_MEH_FORM_REVIEW_SECOND_ROUTES.ANY_LEVEL,
]

export default {
  name: 'BLayoutLK',
  components: {
    LKProgressCircular,
    LKLayout,
    LKHeader,
    NotificationsMenu: () => import(
      /* webpackChunkName: "notifications.menu" */
      '@/views/Notifications/NotificationsMenu.vue'
    ),
    LKAsideMenu,
    LKBanners,
    LKFooter,
  },
  props: {
    ready: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    const isActiveBanners = StorageService.lkNewsButton.get()

    return {
      isActiveBanners: isActiveBanners !== null ? isActiveBanners : true,
    }
  },
  computed: {
    ...rootMapper.mapGetters([
      'profile',
      'authorized',
    ]),
    ...lkMapper.mapState([
      'topics',
      'banners',
      'newBannersCount',
      'munMehCount',
      'regMehCount',
      'roadmapsCount',
      'isLoadingMunMehCount',
      'isLoadingRgMehCount',
      'isLoadingRoadmapsCount',
    ]),
    ...lkMapper.mapGetters([
      'isAdresat',
      'isCurator',
      'isFederalCoordinator',
      'isRegionCoordinator',
      'isMunicipalCoordinator',
    ]),

    hasAccessToReport() {
      return [
        Permissions.review, // куратор
        Permissions.read, // наблюдатель
      ].some(this.$access)
    },
    accessToScan() {
      const accessPermitions = []
      if (this.$access(ixoraScan.read)) accessPermitions.push('ixora')
      return accessPermitions
    },
    hasAsideMenu() {
      const isReport = this.$route.matched
        .some((_) => EXCLUDE_ASIDE.includes(_.name))

      return !isReport
    },
    userPermissionLabel() {
      const hasAccessToLabel = [
        LK_REPORT_ROUTES.LAYOUT,
        LK_ROADMAPS_FEED_ROUTES.LAYOUT,
      ].some((name) => this.$route.name?.includes(name))

      if (!hasAccessToLabel) return ''
      if (!this.authorized) return ''
      if (this.isAdresat) return LABELS.ADRESAT
      if (this.isCurator) return LABELS.CURATOR
      if (
        this.isFederalCoordinator
        || this.isRegionCoordinator
        || this.isMunicipalCoordinator
      ) return LABELS.COORDINATOR

      return ''
    },
  },
  watch: {
    banners(v) {
      if (v.length <= 0) {
        StorageService.lkNewsButton.set(false)
      }
    },
  },
  created() {
    document.body.classList.add('layout-lk')
    document.title = getProjectName()
  },
  destroyed() {
    document.body.classList.remove('layout-lk')
  },
  methods: {
    ...lkMapper.mapActions([
      'readBanner',
    ]),

    toggleBanners(value, listActive) {
      StorageService.lkNewsButton.set(value)
      if (value) {
        this.onBannersView(listActive)
      }
    },
    onBannersView(listActive) {
      listActive.forEach(({ id }) => this.readBanner(id))
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/transitions/transition-fade.scss"></style>

<style lang="scss">
.BLayoutLK {
  @import "~@/assets/scss/webflow/fisoko.webflow";

  font-family: $webflow-font-family;
  font-size: 14px;
  line-height: 20px;
  color: $webflow-color-black;

  &__notifications {
    height: 100%;
  }

  &__user-label {
    margin-bottom: 5px;
    font-weight: 700;
    text-align: right;
  }
}
</style>
